import { parseSearchWith, stringifySearchWith } from '@tanstack/react-router';
import { scrambleId, unscrambleId } from './id-scramble';
import { decodeProductId, encodeProductId } from './product-url-codec';

/**
 * URL-boundary codec that keeps the global sequential run id out of the address
 * bar. A customer must never be able to read another tenant's run *volume* off a
 * URL (Company A's `?dossier_run_id=212` next to Company B's `213`).
 *
 * How it works: a single pair of `parseSearch` / `stringifySearch` functions is
 * handed to `createRouter`. On the way OUT we replace the value of any
 * `*_run_id` search key with a scrambled opaque token; on the way IN we turn it
 * back into the number. EVERYTHING ELSE is handed untouched to TanStack's exact
 * default (de)serializer, so no other route's search params change behaviour.
 *
 * This is obfuscation, not encryption (industry-standard for "don't leak the
 * counter" — same class as Hashids/Sqids); the real data guard is the tenant
 * ownership filter on the run-detail endpoints. The internal global `id` is
 * untouched everywhere — only its outward URL representation changes.
 *
 * Backward compatible: a legacy `?dossier_run_id=129` (raw number) still
 * resolves, because a numeric value is treated as an already-decoded id.
 */

// TanStack's REAL default search (de)serializer. `stringifySearchWith`'s second
// arg (the parser) keeps non-JSON strings — like our `r…` token — UNQUOTED, so
// the URL stays clean (`?dossier_run_id=r1a2b3`, not `%22r1a2b3%22`).
const defaultParse = parseSearchWith(JSON.parse);
const defaultStringify = stringifySearchWith(JSON.stringify, JSON.parse);

// The reversible id scramble lives in id-scramble.ts (shared with product ids).
const TOKEN_PREFIX = 'r'; // marks a value as an encoded token vs a legacy raw id

export function encodeRunId(id: number): string {
  return TOKEN_PREFIX + scrambleId(id);
}

/** Decode a URL run-id value back to the numeric id. A number (legacy raw link)
 *  passes through; a `r…` token is unscrambled; anything else → null. */
export function decodeRunId(raw: string | number): number | null {
  if (typeof raw === 'number') return Number.isInteger(raw) ? raw : null;
  const match = /^r([0-9a-z]+)$/.exec(raw);
  if (match) {
    return unscrambleId(match[1]!);
  }
  const asNum = Number(raw); // tolerate a numeric string from a legacy link
  return Number.isInteger(asNum) ? asNum : null;
}

/** Search keys whose value is a global product id (the wizard's `?product_id=`). */
const isProductIdKey = (key: string): boolean => key === 'product_id';

// A search key whose value is a global run id (run_id, dossier_run_id, …).
// dispatch_group_id (a UUID, already opaque) deliberately does NOT match.
const isRunIdKey = (key: string): boolean => /run_id$/.test(key);

export function stringifySearch(search: Record<string, unknown>): string {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(search)) {
    if (isRunIdKey(key) && typeof value === 'number') out[key] = encodeRunId(value);
    else if (isProductIdKey(key) && typeof value === 'number') out[key] = encodeProductId(value);
    else out[key] = value;
  }
  return defaultStringify(out);
}

export function parseSearch(searchStr: string): Record<string, unknown> {
  const obj = defaultParse(searchStr) as Record<string, unknown>;
  for (const [key, value] of Object.entries(obj)) {
    if (isRunIdKey(key) && typeof value === 'string') {
      const decoded = decodeRunId(value);
      if (decoded !== null) obj[key] = decoded;
    } else if (isProductIdKey(key) && typeof value === 'string') {
      const decoded = decodeProductId(value);
      if (decoded !== null) obj[key] = decoded;
    }
  }
  return obj;
}
